import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../AdminPortal.module.scss';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { useM2MApiContext } from '../../../api/m2mApi';
import { TpOrganizationInfo, TpPluginInfo } from '../../../api/models/admin';
import { promptCreateOrganization, promptDeleteOrganization } from '../actions/m2mCrudOrganization';
import { useUser } from '../../../app/hooks';
import { OrganizationsPluginModal } from '../actions/m2mOrganizationPluginModal';
import { promptAddUserToSelectedOrganization, promptCreateUser } from '../actions/m2mCrudUser';
import { TpUserInfo } from '../../../data/interfaces';
import OrganizationDetails from './OrganizationDetails';
import CleanupButton from './CleanupButton';
import IngestDataButton from './IngestDataButton';
import ManagePluginsButton from './ManagePluginsButton';
import SyncCatalogButton from './SyncCatalogButton';
import ExportDataButton from './ExportDataButton';
import { UserOrganizations } from './UserOrganizations';

interface TpAdminOrganizationsProps {
  organizations: TpOrganizationInfo[];
  plugins: TpPluginInfo[];
  updateOrganizations: () => void;
  updateUsers: () => void;
  users: TpUserInfo[];
}

const AdminOrganizations = (props: TpAdminOrganizationsProps) => {
  const { organizations, plugins, updateOrganizations, updateUsers, users } = props;
  const m2MApi = useM2MApiContext();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const activeOrganization = user.organizations.find((org) => org.isActive);

  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  const handleToggleExpand = (organizationId: string) => {
    setExpandedOrg((prevId) => (prevId === organizationId ? null : organizationId));
  };

  const toggleLoading = (enable: boolean, message: string = '') => {
    setIsLoading(enable);
    setLoadingMessage(message);
  };

  const handleCreateOrganization = () => {
    setIsLoading(true);
    setLoadingMessage('Creating new organization...');
    promptCreateOrganization(m2MApi)
      .then((newOrg) => {
        setIsLoading(false);
        setLoadingMessage('');
        updateOrganizations();
      })
      .catch((err) => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };

  const handleAddNewUser = (organization: TpOrganizationInfo) => {
    setIsLoading(true);
    setLoadingMessage('Creating User ...');
    promptCreateUser(m2MApi, organizations, organization)
      .then((newUser) => {
        setIsLoading(false);
        setLoadingMessage('');
        updateUsers();
        updateOrganizations();
      })
      .catch((err) => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };

  const handleAddExistingUser = (organization: TpOrganizationInfo) => {
    setIsLoading(true);
    setLoadingMessage(`Adding User  to ${organization.name}...`);
    const possibleUsers = users.filter((usr) => {
      return !organization.users?.map((u) => u.email).includes(usr.email as string);
    });
    promptAddUserToSelectedOrganization(m2MApi, possibleUsers, organization)
      .then((newUser) => {
        setIsLoading(false);
        setLoadingMessage('');
        updateUsers();
        updateOrganizations();
      })
      .catch((err) => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };

  /**
   * Delete a given organization after user confirmation
   * @param organization
   */
  const handleDeleteOrganization = (organization: TpOrganizationInfo) => {
    setIsLoading(true);
    setLoadingMessage(`Deleting organization ${organization.name}...`);
    promptDeleteOrganization(m2MApi, organization)
      .then(() => {
        updateOrganizations();
      })
      .catch((err) => {
        if (err === undefined) {
          return; // Ignore cancellation errors
        }
        let message = 'An unknown error occurred while deleting the organization';
        if (typeof err === 'string') {
          message = err;
        } else if (typeof err === 'object') {
          message = err.response?.data?.detail?.detail || err?.message;
        }
        showErrorMessage(message);
      })
      .finally(() => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };

  return (
    <>
      <OrganizationsPluginModal />
      <div style={{ paddingTop: '25px' }}>
        <FancyButton iconName="fas:plus-circle" onClick={handleCreateOrganization}>
          Create New Organization
        </FancyButton>

        <h2 className={styles.subtitle}>Overview ({organizations.length})</h2>
        {isLoading && (
          <div className={styles.loadingBox}>
            <span className={styles.loadingMessage}>{loadingMessage}</span>
          </div>
        )}
        {!!organizations && organizations.length > 0 && (
          <div className={styles.organizationList}>
            {organizations.map((org: TpOrganizationInfo) => (
              <div
                key={org.identifier}
                className={`${styles.organizationItem} ${expandedOrg === org.identifier ? styles.expanded : ''}`}
              >
                <div className={styles.organizationHeader}>
                  <div className={styles.organizationInfo}>
                    <span className={styles.organizationName}>
                      {org.name}
                      {!!activeOrganization && activeOrganization.organizationId === org.identifier && (
                        <span className={styles.activeOrganization}> (Currently Active)</span>
                      )}
                    </span>
                    <span className={styles.organizationId}>ID: {org.identifier}</span>
                  </div>
                  {!!org.identifier && (
                    <div>
                      <FontAwesomeIcon
                        icon={expandedOrg === org.identifier ? 'minimize' : 'maximize'}
                        className={styles.actionIcon}
                        title={expandedOrg === org.identifier ? 'Collapse' : 'Expand'}
                        onClick={() => handleToggleExpand(org.identifier!)}
                      />
                    </div>
                  )}
                </div>
                {expandedOrg === org.identifier && (
                  <div className={styles.expandedContent}>
                    <div className={styles.organizationActionBlock}>
                      <FancyButton iconName="fas:user-plus" onClick={() => handleAddNewUser(org)}>
                        Create New User
                      </FancyButton>
                      <ManagePluginsButton
                        organization={org}
                        plugins={plugins}
                        adminApi={m2MApi.systemAdmin}
                        toggleLoading={toggleLoading}
                        updateOrganizations={updateOrganizations}
                      />
                      <FancyButton iconName="fas:trash-can" onClick={() => handleDeleteOrganization(org)}>
                        Delete Organization
                      </FancyButton>
                      {!!users &&
                        users.filter((usr) => {
                          return !org.users?.map((u) => u.email).includes(usr.email as string);
                        }).length > 0 && (
                          <FancyButton iconName="fas:users-medical" onClick={() => handleAddExistingUser(org)}>
                            Add Existing User
                          </FancyButton>
                        )}
                      {!!activeOrganization && activeOrganization.organizationId === org.identifier && (
                        <>
                          <IngestDataButton organization={org} adminApi={m2MApi.systemAdmin} />
                          <ExportDataButton organization={org} adminApi={m2MApi.systemAdmin} />
                          <CleanupButton organization={org} adminApi={m2MApi.systemAdmin} />
                          <SyncCatalogButton organization={org} adminApi={m2MApi.systemAdmin} />
                        </>
                      )}
                    </div>
                    <OrganizationDetails organization={org} plugins={plugins} listUsers={false} />
                    <UserOrganizations
                      users={users}
                      organizations={[org]}
                      selectedOrganization={org.identifier}
                      allowModifyUser={true}
                      allowRemoveUser={true}
                      allowDeleteUser={true}
                      setIsLoading={toggleLoading}
                      updateUsers={updateUsers}
                      updateOrganizations={updateOrganizations}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminOrganizations;
